import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { AuthService } from 'app/core/auth/auth.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { RoleService } from 'app/core/roles/role.service';
import { Observable, ReplaySubject, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _authService = inject(AuthService);
    private _httpClient = inject(HttpClient);
    private _roleService = inject(RoleService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                navigation.compact = this._filterByRole(navigation.compact);
                navigation.default = this._filterByRole(navigation.default);
                navigation.futuristic = this._filterByRole(navigation.futuristic);
                navigation.horizontal = this._filterByRole(navigation.horizontal);

                this._navigation.next(navigation);
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Recursively drop any item (and its children) whose meta.roles the
     * signed-in user doesn't have at least one of. Items without meta.roles
     * are always kept, and items with meta.supportUsers are also kept for
     * Dankospark support staff.
     *
     * @param items
     */
    private _filterByRole(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items
            .filter((item) => {
                const roles = item.meta?.roles;
                return (
                    !roles ||
                    this._roleService.isInRole(roles) ||
                    (item.meta?.supportUsers && this._authService.isSupportUser)
                );
            })
            .map((item) => ({
                ...item,
                children: item.children
                    ? this._filterByRole(item.children)
                    : item.children,
            }));
    }
}
